/**
 * EditInvoiceModal + VendorAutocomplete + FormField.
 * Extracted from `Invoices.jsx` (Feb 2026 P2 refactor). Fully self-contained.
 */
import { useEffect, useState, useCallback, useRef } from "react";
import { AlertTriangle, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { formatDateShort } from "@/lib/format";
import { useI18n, categoryLabel } from "@/contexts/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import IvaFields from "@/components/IvaFields";

const CURRENCIES = ["EUR", "USD", "GBP", "MXN", "ARS", "COP", "CLP", "BRL", "CHF", "JPY"];

function VendorAutocomplete({ vendor, taxId, onPick, disabled, lang, t }) {
  const [openField, setOpenField] = useState(null); // "vendor" | "tax_id" | null
  const [suggestions, setSuggestions] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);

  const fetchFor = useCallback((q) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/vendors/suggestions?q=${encodeURIComponent(q || "")}&limit=8`);
        setSuggestions(data?.suggestions || []);
        setActiveIdx(0);
      } catch {
        setSuggestions([]);
      }
    }, 180);
  }, []);

  // Close dropdown when clicking outside.
  useEffect(() => {
    const onDoc = (e) => {
      if (!wrapperRef.current || !wrapperRef.current.contains(e.target)) {
        setOpenField(null);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => () => debounceRef.current && clearTimeout(debounceRef.current), []);

  const handleFocus = (field, currentValue) => {
    if (disabled) return;
    setOpenField(field);
    fetchFor(currentValue);
  };
  const handleChange = (field, value) => {
    onPick({ [field === "vendor" ? "vendor" : "vendor_tax_id"]: value });
    setOpenField(field);
    fetchFor(value);
  };
  const commit = (s) => {
    onPick({ vendor: s.vendor || "", vendor_tax_id: s.vendor_tax_id || "" });
    setOpenField(null);
  };
  const onKeyDown = (e) => {
    if (!openField || suggestions.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, suggestions.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); commit(suggestions[activeIdx]); }
    else if (e.key === "Escape") { setOpenField(null); }
  };

  return (
    <div ref={wrapperRef} className="relative contents">
      <FormField label={t("vendor")}>
        <Input
          value={vendor || ""}
          onChange={(e) => handleChange("vendor", e.target.value)}
          onFocus={() => handleFocus("vendor", vendor)}
          onKeyDown={onKeyDown}
          disabled={disabled}
          data-testid="edit-vendor"
          autoComplete="off"
        />
      </FormField>
      <div className="col-span-2 relative">
        <FormField label={t("vendorTaxId")}>
          <Input
            value={taxId || ""}
            onChange={(e) => handleChange("tax_id", e.target.value.toUpperCase().replace(/\s+/g, ""))}
            onFocus={() => handleFocus("tax_id", taxId)}
            onKeyDown={onKeyDown}
            disabled={disabled}
            data-testid="edit-vendor-tax-id"
            placeholder="B12345678"
            className="font-mono"
            autoComplete="off"
          />
        </FormField>
        {openField && suggestions.length > 0 && (
          <div
            className="absolute z-50 left-0 right-0 -mt-1 max-h-72 overflow-y-auto rounded-md border border-[var(--border-color)] bg-[var(--surface)] shadow-lg"
            data-testid="vendor-autocomplete-dropdown"
          >
            {suggestions.map((s, i) => (
              <button
                key={`${s.vendor_tax_id || ""}::${s.vendor || ""}`}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); commit(s); }}
                onMouseEnter={() => setActiveIdx(i)}
                data-testid={`vendor-suggestion-${i}`}
                className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 ${
                  i === activeIdx ? "bg-[var(--surface-hover)]" : ""
                } hover:bg-[var(--surface-hover)]`}
              >
                <span className="flex-1 min-w-0 truncate">
                  <span className="font-medium">{s.vendor || (lang === "es" ? "(sin nombre)" : "(unnamed)")}</span>
                  {s.vendor_tax_id && (
                    <span className="ml-2 font-mono text-xs text-[var(--text-muted)]">{s.vendor_tax_id}</span>
                  )}
                </span>
                <span className="text-[10px] text-[var(--text-muted)] shrink-0 uppercase tracking-wider">
                  {s.count} {lang === "es" ? "fact." : "inv."}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


function EditInvoiceModal({ invoice, categories, onClose, onSaved, onCategoriesChange }) {  const { t, lang } = useI18n();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  // Iter 62 — some callers (BulkUploadModal) don't have the category list
  // loaded and pass `[]`. Fetch on demand so the category picker is always
  // usable regardless of the entry point.
  const [fetchedCats, setFetchedCats] = useState([]);
  const cats = (categories && categories.length > 0) ? categories : fetchedCats;

  // Iter 82 — inline "New category" input inside the category picker.
  const [newCatOpen, setNewCatOpen] = useState(false);
  const [newCatName, setNewCatName] = useState("");
  const [creatingCat, setCreatingCat] = useState(false);

  const createNewCategoryInline = async () => {
    const name = (newCatName || "").trim();
    if (!name) return;
    setCreatingCat(true);
    try {
      // Best-effort kind detection based on the current invoice state so the
      // new category ends up in the same taxonomy bucket.
      const currentKind = (cats.find((c) => c.name === form?.category) || {}).kind;
      const kind = currentKind === "salary"
        ? "salary"
        : form?.type === "income" ? "income" : "other";
      await api.post("/categories", { name, label_es: name, label_en: name, kind });
      toast.success(lang === "es" ? "Categoría creada" : "Category created");
      setForm({ ...form, category: name.toLowerCase() });
      setNewCatName("");
      setNewCatOpen(false);
      if (onCategoriesChange) onCategoriesChange(name.toLowerCase());
      try {
        const { data } = await api.get("/categories");
        setFetchedCats(data || []);
      } catch { /* silent */ }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCreatingCat(false);
    }
  };

  useEffect(() => {
    if (categories && categories.length > 0) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/categories");
        if (!cancelled) setFetchedCats(data || []);
      } catch {
        // Silent — fall back to just the invoice's current category being
        // preserved on save (no options in the dropdown).
      }
    })();
    return () => { cancelled = true; };
  }, [categories]);

  useEffect(() => {
    if (invoice) {
      setForm({
        vendor: invoice.vendor || "",
        vendor_tax_id: invoice.vendor_tax_id || "",
        date: invoice.date || "",
        amount: invoice.amount != null ? String(invoice.amount) : "",
        subtotal: invoice.subtotal != null ? String(invoice.subtotal) : "",
        tax_rate: invoice.tax_rate != null ? String(invoice.tax_rate) : "21",
        currency: invoice.currency || "EUR",
        category: invoice.category || "other",
        type: invoice.type || "expense",
        is_credit_note: !!invoice.is_credit_note,
        invoice_number: invoice.invoice_number || "",
        tax: invoice.tax != null ? String(invoice.tax) : "",
        notes: invoice.notes || "",
        line_items: (invoice.line_items || []).map((li, i) => ({
          _uid: li._uid || `${invoice.id}-${i}`,
          name: li.name || "",
          qty: li.qty != null ? String(li.qty) : "1",
          unit_price: li.unit_price != null ? String(li.unit_price) : "",
          total: li.total != null ? String(li.total) : "",
          // Iter 82 (3.k) — persistir metadatos de precio tarifa/descuento.
          list_price: li.list_price != null ? li.list_price : null,
          discount_pct: li.discount_pct != null ? li.discount_pct : null,
          effective_price: li.effective_price != null ? li.effective_price : null,
        })),
      });
    }
  }, [invoice]);

  if (!invoice || !form) return null;

  // Hide CIF/NIF + relabel "Proveedor → Concepto" for income or salary rows.
  const _editCatKind = (cats.find((c) => c.name === form.category) || {}).kind;
  const _editIsIncome = form.type === "income";
  const _editIsSalary = form.type === "expense" && _editCatKind === "salary";
  const _editVendorLabel = (_editIsIncome || _editIsSalary) ? t("concept") : t("vendor");
  const _editHideTaxId = _editIsIncome || _editIsSalary;

  const updateLineItem = (idx, field, value) => {
    setForm((prev) => {
      const items = [...prev.line_items];
      items[idx] = { ...items[idx], [field]: value };
      return { ...prev, line_items: items };
    });
  };
  const addLineItem = () => {
    setForm((prev) => ({
      ...prev,
      line_items: [...prev.line_items, { _uid: `new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, name: "", qty: "1", unit_price: "", total: "" }],
    }));
  };
  const removeLineItem = (idx) => {
    setForm((prev) => ({
      ...prev,
      line_items: prev.line_items.filter((_, i) => i !== idx),
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const cleanItems = form.line_items
        .filter((li) => li.name.trim())
        .map((li) => ({
          name: li.name.trim(),
          qty: li.qty ? parseFloat(li.qty) : 1,
          unit_price: li.unit_price ? parseFloat(li.unit_price) : null,
          total: li.total ? parseFloat(li.total) : null,
          // Preservar datos tarifa/descuento cuando existen — el backend
          // recalcula `effective_price` en `_annotate_line_items`.
          list_price: li.list_price != null && li.list_price !== "" ? parseFloat(li.list_price) : null,
          discount_pct: li.discount_pct != null && li.discount_pct !== "" ? parseFloat(li.discount_pct) : null,
        }));
      await api.put(`/invoices/${invoice.id}`, {
        ...form,
        amount: form.amount ? parseFloat(form.amount) : null,
        subtotal: form.subtotal ? parseFloat(form.subtotal) : null,
        tax: form.tax ? parseFloat(form.tax) : null,
        tax_rate: form.tax_rate !== "" && form.tax_rate != null ? parseFloat(form.tax_rate) : null,
        vendor_tax_id: form.vendor_tax_id || null,
        is_credit_note: !!form.is_credit_note,
        line_items: cleanItems,
      });

      // If the category changed AND the invoice has a vendor with more entries,
      // AUTO-propagate the new category across all of that vendor's invoices.
      // Real user flow: once you re-classify one Frigorificos invoice as
      // "groceries", you almost always want the other 30 rows to follow. We
      // used to prompt via window.confirm — that friction was unnecessary.
      // The toast confirms what happened so the user can spot mistakes.
      const categoryChanged = (invoice.category || "other") !== (form.category || "other");
      const hasVendor = !!(form.vendor?.trim() || form.vendor_tax_id?.trim());
      if (categoryChanged && hasVendor && form.type === "expense") {
        try {
          const { data: applied } = await api.post("/invoices/apply-category-to-vendor", {
            invoice_id: invoice.id,
            category: form.category,
          });
          const others = Math.max(0, (applied?.modified || 1) - 1);
          if (others > 0) {
            const vendorLabel = form.vendor?.trim() || form.vendor_tax_id;
            toast.success(
              lang === "es"
                ? `Categoría "${form.category}" aplicada a ${others} factura(s) más de ${vendorLabel}`
                : `Category "${form.category}" applied to ${others} more invoice(s) of ${vendorLabel}`,
              { duration: 5000 }
            );
          }
        } catch (e) {
          // Don't block the save if the propagation fails silently.
          console.warn("[apply-category-to-vendor]", e);
        }
      }

      toast.success(t("saved"));
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!invoice} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-invoice-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{t("edit")}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 mt-2">
          {_editIsIncome || _editIsSalary ? (
            // Income / salary rows use "concept" (a plain text field, no
            // vendor autocomplete because there's no CIF-based supplier).
            <FormField label={_editVendorLabel}>
              <Input value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} data-testid="edit-vendor" />
            </FormField>
          ) : (
            // Expense (real vendor) — autocomplete on vendor name AND CIF,
            // picking either fills both fields from history.
            <VendorAutocomplete
              vendor={form.vendor}
              taxId={form.vendor_tax_id}
              onPick={(patch) => setForm((prev) => ({ ...prev, ...patch }))}
              lang={lang}
              t={t}
            />
          )}
          <FormField label={_editIsSalary ? t("salaryDate") : t("invoiceDate")}>
            <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="edit-date" />
            {/* When the OCR captured the raw date snippet, show it here so the
                user can compare what the AI read vs the actual invoice. Also
                flag the "date_missing" case: OCR fell back to upload date. */}
            {invoice.date_missing ? (
              <p className="text-[11px] text-[var(--status-warning)] mt-1 flex items-center gap-1">
                <AlertTriangle size={11} />
                {lang === "es"
                  ? "La IA no detectó una fecha clara — se usó la fecha de subida. Corrígela con la fecha real de la factura."
                  : "AI couldn't detect a clear date — using upload date. Please correct with the real invoice date."}
              </p>
            ) : invoice.date_source ? (
              <p className="text-[10px] text-[var(--text-muted)] mt-1 font-mono">
                {lang === "es" ? "IA detectó:" : "AI detected:"} <span className="italic">{invoice.date_source}</span>
              </p>
            ) : null}
            {invoice.created_at && (
              <p className="text-[10px] text-[var(--text-muted)] mt-0.5">
                {lang === "es" ? "Fecha de subida:" : "Upload date:"} {formatDateShort(invoice.created_at)}
              </p>
            )}
          </FormField>
          {/* CIF input for income/salary rows is intentionally hidden; the
              VendorAutocomplete above renders both vendor + CIF for expenses. */}
          {!_editIsSalary && (
            <div className="col-span-2">
              <FormField label={t("invoiceNumber")}>
                <Input
                  value={form.invoice_number}
                  onChange={(e) => setForm({ ...form, invoice_number: e.target.value })}
                  data-testid="edit-invoice-number"
                  placeholder="F-2026/0001"
                  className="font-mono"
                />
              </FormField>
            </div>
          )}

          <div className="col-span-2 flex items-center gap-2 -mb-2">
            <input
              id="edit-is-credit-note"
              type="checkbox"
              checked={!!form.is_credit_note}
              onChange={(e) => setForm({ ...form, is_credit_note: e.target.checked })}
              data-testid="edit-is-credit-note"
              className="rounded border-[var(--border-color)] text-[var(--primary)] focus:ring-[var(--primary)]"
            />
            <Label htmlFor="edit-is-credit-note" className="text-sm font-normal cursor-pointer" title={t("creditNoteHelp")}>
              {t("creditNote")}
            </Label>
          </div>

          {!_editIsSalary && (
            <IvaFields
              values={{ subtotal: form.subtotal, tax: form.tax, amount: form.amount, tax_rate: form.tax_rate }}
              onChange={(patch) => setForm((prev) => ({ ...prev, ...patch }))}
              testidPrefix="edit-iva"
            />
          )}

          {_editIsSalary && (
            <div className="col-span-2">
              <FormField label={lang === "es" ? "Importe neto" : "Net amount"}>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.amount}
                  onChange={(e) => {
                    const v = e.target.value;
                    setForm((prev) => ({
                      ...prev,
                      amount: v,
                      subtotal: v,
                      tax: "0",
                      tax_rate: "0",
                    }));
                  }}
                  data-testid="edit-salary-amount"
                  className="font-mono-num"
                />
              </FormField>
            </div>
          )}

          <FormField label={t("currency")}>
            <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
              <SelectTrigger data-testid="edit-currency"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </FormField>
          {!_editIsIncome && (
            <FormField label={t("category")}>
              {newCatOpen ? (
                <div className="flex gap-2">
                  <Input
                    autoFocus
                    value={newCatName}
                    onChange={(e) => setNewCatName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") { e.preventDefault(); createNewCategoryInline(); }
                      else if (e.key === "Escape") { setNewCatOpen(false); setNewCatName(""); }
                    }}
                    placeholder={lang === "es" ? "Nombre" : "Name"}
                    data-testid="edit-new-category-name"
                    disabled={creatingCat}
                  />
                  <Button
                    type="button"
                    size="sm"
                    onClick={createNewCategoryInline}
                    disabled={creatingCat || !newCatName.trim()}
                    data-testid="edit-save-new-category-btn"
                    className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white shrink-0"
                  >
                    {creatingCat ? "..." : (lang === "es" ? "Crear" : "Create")}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => { setNewCatOpen(false); setNewCatName(""); }}
                    disabled={creatingCat}
                    className="shrink-0"
                  >
                    {t("cancel")}
                  </Button>
                </div>
              ) : (
                <Select
                  value={form.category}
                  onValueChange={(v) => {
                    if (v === "__new__") setNewCatOpen(true);
                    else setForm({ ...form, category: v });
                  }}
                >
                  <SelectTrigger data-testid="edit-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {cats.map((c) => <SelectItem key={c.name} value={c.name}>{categoryLabel(c, lang)}</SelectItem>)}
                    <SelectItem value="__new__" data-testid="edit-new-category-option">
                      <span className="text-[var(--primary)]">
                        {lang === "es" ? "+ Nueva categoría…" : "+ New category…"}
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              )}
            </FormField>
          )}
          <FormField label={t("type")}>
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
              <SelectTrigger data-testid="edit-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="expense">{t("expense")}</SelectItem>
                <SelectItem value="income">{t("income")}</SelectItem>
              </SelectContent>
            </Select>
          </FormField>
          <div className="col-span-2">
            <FormField label={t("notes")}>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </FormField>
          </div>
          <div className="col-span-2 border-t border-[var(--border-color)] pt-4 mt-2">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs uppercase tracking-wider text-[var(--text-secondary)]">{t("lineItems")}</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addLineItem}
                className="h-7 text-xs"
                data-testid="edit-add-line-item"
              >
                + {t("addLineItem")}
              </Button>
            </div>
            {form.line_items.length === 0 ? (
              <p className="text-xs text-[var(--text-muted)] py-2">—</p>
            ) : (
              <div className="space-y-2">
                {form.line_items.map((li, idx) => {
                  // Iter 82 (3.k) — precio tarifa (list_price) vs precio
                  // cobrado (effective_price = total/qty). Solo mostramos la
                  // fila secundaria cuando alguno de los dos está presente
                  // y aporta información distinta.
                  const qty = parseFloat(li.qty) || 0;
                  const total = parseFloat(li.total);
                  const listPrice = li.list_price != null ? parseFloat(li.list_price) : null;
                  const effComputed = qty > 0 && !isNaN(total) ? total / qty : null;
                  const eff = li.effective_price != null ? parseFloat(li.effective_price) : effComputed;
                  const hasDiscount = listPrice && eff && listPrice > eff && (listPrice - eff) / listPrice >= 0.005;
                  const dctPct = hasDiscount
                    ? (li.discount_pct != null
                        ? parseFloat(li.discount_pct)
                        : Math.round(100 * (1 - eff / listPrice) * 100) / 100)
                    : null;
                  return (
                    <div key={li._uid || `idx-${idx}`} className="space-y-1" data-testid={`edit-line-item-${idx}`}>
                      <div className="grid grid-cols-12 gap-2 items-center">
                        <Input
                          placeholder={t("productName")}
                          value={li.name}
                          onChange={(e) => updateLineItem(idx, "name", e.target.value)}
                          className="col-span-5 h-8 text-sm"
                          data-testid={`edit-li-name-${idx}`}
                        />
                        <Input
                          type="number"
                          step="0.01"
                          placeholder={t("qty")}
                          value={li.qty}
                          onChange={(e) => updateLineItem(idx, "qty", e.target.value)}
                          className="col-span-2 h-8 text-sm"
                          data-testid={`edit-li-qty-${idx}`}
                        />
                        <Input
                          type="number"
                          step="0.01"
                          placeholder={t("unitPrice")}
                          value={li.unit_price}
                          onChange={(e) => updateLineItem(idx, "unit_price", e.target.value)}
                          className="col-span-2 h-8 text-sm"
                          data-testid={`edit-li-unit-${idx}`}
                        />
                        <Input
                          type="number"
                          step="0.01"
                          placeholder={t("lineTotal")}
                          value={li.total}
                          onChange={(e) => updateLineItem(idx, "total", e.target.value)}
                          className="col-span-2 h-8 text-sm"
                          data-testid={`edit-li-total-${idx}`}
                        />
                        <button
                          type="button"
                          onClick={() => removeLineItem(idx)}
                          className="col-span-1 text-[var(--text-muted)] hover:text-[var(--status-negative)] transition-colors"
                          data-testid={`edit-li-remove-${idx}`}
                          aria-label="remove line item"
                        >
                          <X size={14} />
                        </button>
                      </div>
                      {(hasDiscount || (eff != null && !isNaN(eff))) && (
                        <div
                          className="pl-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-[var(--text-muted)] font-mono-num"
                          data-testid={`edit-li-pricing-${idx}`}
                        >
                          {listPrice != null && hasDiscount && (
                            <span data-testid={`edit-li-list-price-${idx}`}>
                              {lang === "es" ? "Tarifa" : "List"}: {listPrice.toFixed(2)} €
                            </span>
                          )}
                          {hasDiscount && dctPct != null && (
                            <span
                              className="text-[var(--status-negative)] font-medium"
                              data-testid={`edit-li-discount-${idx}`}
                            >
                              −{dctPct}%
                            </span>
                          )}
                          {eff != null && !isNaN(eff) && (
                            <span data-testid={`edit-li-effective-${idx}`}>
                              {lang === "es" ? "Cobrado/ud" : "Paid/unit"}: <b className="text-[var(--text-primary)]">{eff.toFixed(4).replace(/\.?0+$/, "")}</b> €
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("cancel")}</Button>
          <Button onClick={save} disabled={saving} data-testid="edit-save-btn" className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white">
            {saving ? "..." : t("save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FormField({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-wider text-[var(--text-secondary)]">{label}</Label>
      {children}
    </div>
  );
}

export { EditInvoiceModal, VendorAutocomplete, FormField };
